/**
 * Insights panel — AI-synthesized daily intelligence brief.
 *
 * Subscribes to analysis:complete events and displays a structured brief:
 * Key Developments (top 5 focal points), Surge Alerts (active surges),
 * Geographic Convergence (active convergence cells), Risk Summary (top 5 CII).
 *
 * Auto-refreshes on each 60-second analysis cycle.
 */

import { eventBus } from '@/core/events';
import { getAnalysisEngine } from '@/intelligence/analysis/engine';
import type {
  FocalPoint,
  SurgeEvent,
} from '@/intelligence/analysis/types';

export function createInsightsPanel(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'insights-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto;';

  const contentArea = document.createElement('div');
  contentArea.style.cssText = 'padding: 12px;';
  container.appendChild(contentArea);

  const cleanupFns: (() => void)[] = [];
  let renderPending = false;

  function scheduleRender(): void {
    if (renderPending) return;
    renderPending = true;
    requestAnimationFrame(() => {
      renderPending = false;
      render();
    });
  }

  function renderEmpty(): void {
    contentArea.textContent = '';
    const msg = document.createElement('div');
    msg.style.cssText = 'padding: 32px 16px; text-align: center; color: var(--text-muted); font-size: 12px;';
    msg.textContent = 'Waiting for analysis cycle to complete\u2026';
    contentArea.appendChild(msg);
  }

  function render(): void {
    const engine = getAnalysisEngine();
    const result = engine?.getLastResult();
    if (!result) {
      renderEmpty();
      return;
    }

    contentArea.textContent = '';

    // Timestamp header
    const ts = document.createElement('div');
    ts.style.cssText = 'font-size: 10px; color: var(--text-muted); margin-bottom: 12px; text-align: right;';
    const d = new Date(result.timestamp);
    ts.textContent = `Last analysis: ${d.getUTCHours().toString().padStart(2, '0')}:${d.getUTCMinutes().toString().padStart(2, '0')} UTC`;
    contentArea.appendChild(ts);

    // --- Key Developments (top 5 focal points) ---
    contentArea.appendChild(renderFocalPoints(result.focalPoints));

    // --- Surge Alerts ---
    contentArea.appendChild(renderSurges(result.surges));
  }

  function renderSectionHeader(text: string): HTMLElement {
    const header = document.createElement('div');
    header.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.05em;';
    header.textContent = text;
    return header;
  }

  function renderFocalPoints(focalPoints: FocalPoint[]): HTMLElement {
    const section = document.createElement('div');
    section.style.cssText = 'margin-bottom: 16px;';
    section.appendChild(renderSectionHeader(`Key Developments (${focalPoints.length})`));

    if (focalPoints.length === 0) {
      const empty = document.createElement('div');
      empty.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic;';
      empty.textContent = 'No focal points detected';
      section.appendChild(empty);
      return section;
    }

    const top5 = focalPoints
      .slice()
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);

    for (const fp of top5) {
      const card = document.createElement('div');
      card.style.cssText = `
        padding: 8px 10px; margin-bottom: 6px; border-radius: 6px;
        background: var(--bg-secondary); border: 1px solid var(--border-primary);
      `;

      // Entity + urgency badge
      const titleRow = document.createElement('div');
      titleRow.style.cssText = 'display: flex; align-items: center; gap: 6px; margin-bottom: 4px;';

      const entityName = document.createElement('span');
      entityName.style.cssText = 'font-size: 12px; font-weight: 600; color: var(--text-primary);';
      entityName.textContent = fp.entity;
      titleRow.appendChild(entityName);

      const urgencyBadge = document.createElement('span');
      const urgencyColor = fp.urgencyLevel === 'critical'
        ? 'var(--severity-critical)'
        : fp.urgencyLevel === 'elevated'
          ? 'var(--severity-high)'
          : 'var(--severity-medium)';
      urgencyBadge.style.cssText = `
        font-size: 9px; font-weight: 600; text-transform: uppercase;
        padding: 1px 6px; border-radius: 3px;
        background: ${urgencyColor}22; color: ${urgencyColor};
      `;
      urgencyBadge.textContent = fp.urgencyLevel;
      titleRow.appendChild(urgencyBadge);

      const scoreLabel = document.createElement('span');
      scoreLabel.style.cssText = 'font-size: 10px; font-family: var(--font-mono); color: var(--text-muted); margin-left: auto;';
      scoreLabel.textContent = String(fp.score);
      titleRow.appendChild(scoreLabel);

      card.appendChild(titleRow);

      // Narrative
      if (fp.narrative) {
        const narrative = document.createElement('div');
        narrative.style.cssText = 'font-size: 11px; color: var(--text-secondary); line-height: 1.4;';
        narrative.textContent = fp.narrative;
        card.appendChild(narrative);
      }

      // Mentions + signal count
      const meta = document.createElement('div');
      meta.style.cssText = 'font-size: 10px; color: var(--text-muted); margin-top: 4px;';
      meta.textContent = `${fp.mentionCount} mentions \u00B7 ${fp.signals.length} signals`;
      card.appendChild(meta);

      section.appendChild(card);
    }

    return section;
  }

  function renderSurges(surges: SurgeEvent[]): HTMLElement {
    const section = document.createElement('div');
    section.style.cssText = 'margin-bottom: 16px;';
    section.appendChild(renderSectionHeader(`Surge Alerts (${surges.length})`));

    if (surges.length === 0) {
      const empty = document.createElement('div');
      empty.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic;';
      empty.textContent = 'No active surges';
      section.appendChild(empty);
      return section;
    }

    for (const surge of surges) {
      const row = document.createElement('div');
      row.style.cssText = `
        display: flex; align-items: center; gap: 8px; padding: 6px 10px;
        margin-bottom: 4px; border-radius: 4px; background: var(--bg-secondary);
      `;

      const sourceType = document.createElement('span');
      sourceType.style.cssText = 'font-size: 12px; font-weight: 500; color: var(--text-primary);';
      sourceType.textContent = surge.sourceType;
      row.appendChild(sourceType);

      const region = document.createElement('span');
      region.style.cssText = 'font-size: 11px; color: var(--text-muted);';
      region.textContent = surge.region;
      row.appendChild(region);

      const ratio = document.createElement('span');
      ratio.style.cssText = `
        font-size: 10px; font-weight: 600; font-family: var(--font-mono);
        margin-left: auto;
        color: ${surge.ratio >= 5 ? 'var(--severity-critical)' : 'var(--severity-high)'};
      `;
      ratio.textContent = `${surge.ratio.toFixed(1)}x`;
      row.appendChild(ratio);

      const countLabel = document.createElement('span');
      countLabel.style.cssText = 'font-size: 10px; color: var(--text-muted);';
      countLabel.textContent = `${surge.count}/${surge.baseline}`;
      row.appendChild(countLabel);

      section.appendChild(row);
    }

    return section;
  }

  // Initial render
  renderEmpty();

  // On first load, check if engine already has results
  const engine = getAnalysisEngine();
  if (engine?.getLastResult()) {
    render();
  }

  // Subscribe to analysis:complete for auto-refresh
  cleanupFns.push(
    eventBus.on('analysis:complete', () => scheduleRender()),
  );

  // Expose destroy
  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
  };

  return container;
}
